import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, MessageCircle, Plus, Send, Sparkles, X } from "lucide-react";
import { formatBRL } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
  "Oi! Eu sou a Gigi, atendente do Giga Açaí. Posso tirar dúvidas do cardápio ou montar seu pedido aqui na conversa. O que você procura hoje?";

const getSessionId = () => {
  let id = localStorage.getItem("giga-chat-session");
  if (!id) {
    id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("giga-chat-session", id);
  }
  return id;
};

const visibleText = (c) => c.split("<<<ORDER>>>")[0];

export const ChatWidget = ({ onAdd, lifted }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const patchLast = (patch) =>
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], ...patch };
      return next;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "", streaming: true },
    ]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let ev;
          try {
            ev = JSON.parse(part.slice(6));
          } catch {
            continue;
          }
          if (ev.type === "token") {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: last.content + ev.content };
              return next;
            });
          } else if (ev.type === "items") {
            patchLast({ items: ev.items });
          } else if (ev.type === "error") {
            throw new Error(ev.content);
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "Tive um probleminha agora. Tenta de novo em instantes?",
        };
        return next;
      });
    } finally {
      patchLast({ streaming: false });
      setStreaming(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            type="button"
            data-testid="chat-fab"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            onClick={() => setOpen(true)}
            aria-label="Abrir atendente virtual"
            className={`fixed right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-amber-400 text-[#1E053B] shadow-[0_15px_40px_-10px_rgba(245,158,11,0.6)] transition-all duration-300 hover:scale-110 ${
              lifted ? "bottom-24" : "bottom-5"
            }`}
          >
            <MessageCircle className="h-6 w-6" strokeWidth={2.4} />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            className="fixed inset-x-0 bottom-0 z-[70] flex h-[85vh] flex-col overflow-hidden rounded-t-[1.75rem] border border-purple-500/30 bg-[#2D0B5A] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] sm:inset-x-auto sm:bottom-5 sm:right-5 sm:h-[600px] sm:w-[400px] sm:rounded-[1.75rem]"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between border-b border-purple-500/20 bg-[#1E053B]/70 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400 text-[#1E053B]">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-syne text-base font-extrabold text-white">Gigi</p>
                  <p className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    Atendente virtual · online
                  </p>
                </div>
              </div>
              <button
                type="button"
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Fechar chat"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-purple-500/30 text-purple-200 transition-colors hover:border-red-400 hover:text-red-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "rounded-br-md bg-emerald-400 font-medium text-[#1E053B]"
                        : "rounded-bl-md bg-white/8 text-purple-100"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{visibleText(m.content)}</p>
                    {m.streaming && (
                      <span
                        data-testid="chat-streaming-indicator"
                        className="mt-1 inline-flex items-center gap-1 text-[10px] text-purple-300/60"
                      >
                        <Loader2 className="h-3 w-3 animate-spin" /> digitando...
                      </span>
                    )}
                    {m.items && m.items.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {m.items.map((item, j) => (
                          <div
                            key={j}
                            className="rounded-xl border border-purple-400/25 bg-[#1E053B]/60 p-3"
                          >
                            <p className="text-xs font-bold text-white">{item.title}</p>
                            {item.details && item.details.length > 0 && (
                              <p className="mt-0.5 text-[11px] text-purple-300/70">
                                {item.details.join(", ")}
                              </p>
                            )}
                            <div className="mt-2 flex items-center justify-between">
                              <span className="font-syne text-sm font-extrabold text-emerald-400">
                                {formatBRL(item.unitPrice * item.qty)}
                                {item.qty > 1 && (
                                  <span className="ml-1 text-[10px] text-purple-300/60">
                                    x{item.qty}
                                  </span>
                                )}
                              </span>
                              <button
                                type="button"
                                data-testid={`chat-add-item-${i}-${j}`}
                                onClick={() => {
                                  onAdd({ title: item.title, details: item.details, unitPrice: item.unitPrice, qty: item.qty });
                                }}
                                className="flex items-center gap-1.5 rounded-full bg-emerald-400 px-3.5 py-1.5 text-[11px] font-bold text-[#1E053B] transition-transform duration-200 hover:scale-105 active:scale-95"
                              >
                                <Plus className="h-3 w-3" strokeWidth={3} /> Adicionar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-purple-500/20 bg-[#1E053B]/70 p-4">
              <div className="flex items-center gap-2.5">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Pergunte ou peça seu açaí..."
                  maxLength={500}
                  className="flex-1 rounded-full border border-purple-500/30 bg-white/5 px-5 py-3 text-sm text-white placeholder:text-purple-300/40 outline-none transition-colors duration-200 focus:border-amber-400"
                />
                <button
                  type="button"
                  data-testid="chat-send-button"
                  onClick={send}
                  disabled={streaming || !input.trim()}
                  aria-label="Enviar mensagem"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[#1E053B] transition-transform duration-200 enabled:hover:scale-105 enabled:active:scale-95 disabled:opacity-40"
                >
                  {streaming ? (
                    <Loader2 className="h-4.5 w-4.5 animate-spin" />
                  ) : (
                    <Send className="h-4.5 w-4.5" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
